import React, { useState } from "react";

import { format, parseISO } from "date-fns";

import useGetFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import {
  TaskManipulator,
  TaskManipulatorType,
} from "@fartherfinance/frontend/api/Tasks/Types";

import { formatRelativeTimestamp, getManipulatorNameV2 } from "../utils";
import Avatar from "@src/multiCustodian/components/Avatar/Avatar";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import AdvisorTag from "@src/multiCustodian/pages/Advisor/Tasks/TaskDetails/Components/AdvisorTag/AdvisorTag";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TaskSystemComment.module.css";

interface TaskSystemCommentProps {
  manipulator: TaskManipulator;
  comment: string;
  timestamp: string;
  isAdvisor?: boolean;
}

const TaskSystemComment: React.FC<TaskSystemCommentProps> = ({
  manipulator,
  comment,
  timestamp,
  isAdvisor,
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const auth = useAdvisorRequestAuth();
  const advisors = useGetFartherEmployees("All", auth);

  const authorInitials =
    manipulator.type === TaskManipulatorType.enum.HUMAN && manipulator.name
      ? `${manipulator.name.first[0]}${manipulator.name.last[0]}`
      : undefined;

  const authorAvatarSource =
    manipulator.type === TaskManipulatorType.enum.HUMAN
      ? advisors.data?.advisors.find(
          (advisor) => advisor.advisorId === manipulator.id
        )?.details.headshotPath ?? undefined
      : undefined;

  const parsedTimestamp = parseISO(timestamp);

  const handleMouseEnter = (): void => {
    setIsHovering(true);
  };

  const handleMouseLeave = (): void => {
    setIsHovering(false);
  };

  return (
    <div className={styles.container}>
      <Avatar
        className={styles.avatar}
        imageSource={authorAvatarSource}
        initials={authorInitials}
      />

      <div>
        <Text variant="body" className={styles.manipulator}>
          {getManipulatorNameV2(manipulator)}
          {isAdvisor && <AdvisorTag />}
        </Text>
        <Text variant="body" color="textSubtle" className={styles.comment}>
          &nbsp; {comment}
        </Text>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="2"
          height="2"
          viewBox="0 0 2 2"
          fill="none"
          className={styles.dot}
        >
          <circle cx="1" cy="1" r="1" fill="currentColor" />
        </svg>
        <Text
          variant="body"
          className={styles.timestamp}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isHovering
            ? format(parsedTimestamp, "M/d/yy 'at' h:mm a")
            : formatRelativeTimestamp(parsedTimestamp)}
        </Text>
      </div>
    </div>
  );
};

export default TaskSystemComment;
